import { useState, type FC } from 'react';
import DraggableModal from 'components/organisms/draggableModal';
import { useTranslation } from 'react-i18next';
import BaseButton from 'components/atoms/baseButton';
import { useFormik } from 'formik';
import classNames from 'classnames';
import { typedObjectEntries } from 'infrastructure/functions/typed-object-entries';

import s from './styles.module.scss';
import { steppedTemplates } from './templates/initial';
import FormItem from './components/form-item';
import {
  checkIsErrorExist,
  checkStepErrors,
  reduceInitialDataBySteps,
} from './helpers';
import { generateInitialBlurb } from './blurbs/initial';
import { initialSchema } from './validation';

import type { TInitialTemplateValues } from './templates/initial';

type TSteps = keyof typeof steppedTemplates;

interface InitialApcmTemplateModalProps {
  open: boolean;
  onSubmit: (values: string) => void;
  onClose: () => void;
}

const InitialApcmTemplateModal: FC<InitialApcmTemplateModalProps> = ({
  open,
  onSubmit,
  onClose,
}) => {
  const steps = typedObjectEntries(steppedTemplates).map(
    ([key]) => Number(key) as TSteps,
  );
  const [currentStep, setCurrentStep] = useState<TSteps>(0);
  const { t } = useTranslation();

  const formik = useFormik<TInitialTemplateValues>({
    initialValues: {},
    onSubmit: async (values) => {
      const steppedValues = reduceInitialDataBySteps(values);
      const blurb = generateInitialBlurb(steppedValues);
      onSubmit(blurb);

      onClose();
    },
    validateOnChange: false,
    validationSchema: initialSchema,
  });

  const onSave = async () => {
    await formik.submitForm();
  };

  const handleClose = () => {
    setCurrentStep(0);
    onClose();
  };

  const lastStep = steps.length - 1;
  const disablePrev = currentStep === 0;
  const disableNext = currentStep === lastStep;

  const title = t('apcmTemplate.initialAPCMAssessmentTemplate');
  const subTitle = `${steppedTemplates[currentStep].title} - ${
    currentStep + 1
  } of ${steps.length}`;

  const stepErrors = checkStepErrors(steppedTemplates, formik.errors);

  const modalFooter = (
    <div className={s.footer}>
      {!formik.isValid && stepErrors.length > 0 && (
        <p className={s['error-message']}>
          {t('apcmTemplate.missingFieldsInSection')}:{' '}
          <span>{stepErrors.join(', ')}</span>
        </p>
      )}
      <div>
        <div className={s['step-btns-wrapper']}>
          {!disablePrev && (
            <BaseButton
              type="secondary"
              label={`< ${t('controls.back')}`}
              onClick={() => {
                if (currentStep > 0) {
                  setCurrentStep((prev) => steps[steps.indexOf(prev) - 1]);
                }
              }}
              disabled={disablePrev}
              width={130}
            />
          )}
          {!disableNext && (
            <BaseButton
              type="secondary"
              label={`${t('controls.forward')} >`}
              onClick={() => {
                if (currentStep < lastStep) {
                  setCurrentStep((prev) => steps[steps.indexOf(prev) + 1]);
                }
              }}
              width={130}
              disabled={disableNext}
            />
          )}
        </div>
        <BaseButton
          label={t('controls.save')}
          onClick={() => onSave()}
          width={130}
        />
      </div>
    </div>
  );

  if (!open) return null;

  return (
    <DraggableModal
      title={title}
      subTitle={subTitle}
      isOpen={open}
      toggle={handleClose}
      footer={modalFooter}
    >
      <div className={s['template-wrapper']}>
        {steppedTemplates[currentStep].template.map((formItem) => {
          const error = checkIsErrorExist(formItem, formik.errors);

          return (
            <div
              className={classNames(s.item, { [s.error]: error })}
              key={formItem.key}
            >
              <FormItem item={formItem} formik={formik} />
            </div>
          );
        })}
      </div>
    </DraggableModal>
  );
};
export default InitialApcmTemplateModal;
